import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building, Scale, Anchor, Home, ArrowRight, Award, Users, Clock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Cache busting helper
const fetchWithNoCache = (url) => {
  const bustCache = `?_t=${Date.now()}`;
  return axios.get(`${url}${bustCache}`);
};

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_jamaica-law-firm/artifacts/009fjy5q_8f971d03-85d0-4226-a53a-63c45ad5a7ff.png";

const iconMap = {
  Building: Building,
  Scale: Scale,
  Anchor: Anchor,
  Home: Home,
  Clock: Clock,
  Users: Users,
  Award: Award
};

const defaultServices = [
  { icon: Building, title: "Corporate & Business Law", description: "Navigating complex mergers, acquisitions, and compliance with precision." },
  { icon: Scale, title: "Civil Litigation", description: "Defending your rights in high-stakes disputes and resolutions." },
  { icon: Anchor, title: "Maritime Law", description: "Expertise in international shipping, logistics, and admiralty law." },
  { icon: Home, title: "Real Estate Law", description: "Securing your investments in commercial and residential properties." }
];

const defaultStats = [
  { icon: Clock, value: "23+", label: "Years Experience" },
  { icon: Users, value: "500+", label: "Clients Served" },
  { icon: Award, value: "100%", label: "Dedication" }
];

export const HomePage = () => {
  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const cacheBust = `?_t=${Date.now()}`;
        const [homeRes, servicesRes] = await Promise.all([
          axios.get(`${API}/content/home${cacheBust}`),
          axios.get(`${API}/content/services${cacheBust}`)
        ]);
        setContent(homeRes.data);
        setServices(servicesRes.data || []);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const stats = content?.stats || defaultStats;
  const displayServices = services.length > 0 ? services.slice(0, 4) : defaultServices;

  return (
    <div className="p-8 md:p-12 lg:p-16">
      {/* Hero Section - Large Logo */}
      <section className="text-center mb-20 pt-8" data-testid="hero-section">
        <div className="mb-8 animate-fade-in-up">
          <img 
            src={content?.logo_url || DEFAULT_LOGO} 
            alt="D Brooks Law" 
            className="h-48 md:h-64 lg:h-72 w-auto mx-auto mb-8 drop-shadow-2xl"
            data-testid="hero-logo"
          />
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a1412] leading-tight mb-6 animate-fade-in-up animation-delay-100">
          {content?.headline || "D. Brooks Law"}
        </h1>
        
        <div className="max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
          <p className="font-serif text-xl md:text-2xl text-[#5a4a3a] italic mb-4">
            "{content?.motto || "Excellence in Justice, Integrity in Service"}"
          </p>
          <div className="w-24 h-1 bg-[#C9A86C] mx-auto mb-6"></div>
          <p className="font-body text-lg text-[#7a7065] mb-8">
            {content?.description || "Proudly serving Jamaica with over 23 years of legal excellence in Corporate, Civil, Maritime, and Real Estate Law."}
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up animation-delay-300">
          <Link to="/book" className="btn-primary inline-block" data-testid="hero-book-btn">
            {content?.cta_primary || "Schedule a Consultation"}
          </Link>
          <Link to="/about" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-learn-more-btn">
            {content?.cta_secondary || "Meet Devon Brooks"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Divider */}
      <div className="flex items-center justify-center gap-4 mb-16">
        <div className="h-px bg-[#C9A86C]/30 flex-1"></div>
        <Scale className="text-[#C9A86C]" size={24} />
        <div className="h-px bg-[#C9A86C]/30 flex-1"></div>
      </div>

      {/* Stats Section */}
      <section className="grid grid-cols-3 gap-4 md:gap-8 mb-16" data-testid="stats-section">
        {stats.map((stat, index) => {
          const IconComponent = iconMap[stat.icon] || Clock;
          return (
            <div key={index} className="text-center p-6 bg-white/50 rounded-lg border border-[#C9A86C]/20">
              <IconComponent className="mx-auto mb-3 text-[#C9A86C]" size={32} />
              <p className="font-heading text-3xl md:text-4xl font-bold text-[#1a1412]">{stat.value}</p>
              <p className="font-body text-xs md:text-sm text-[#7a7065] uppercase tracking-wider mt-1">{stat.label}</p>
            </div>
          );
        })}
      </section>

      {/* Services Preview */}
      <section className="mb-16" data-testid="services-preview">
        <div className="text-center mb-10">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C9A86C] mb-2">Our Expertise</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1a1412] mb-3">
            {content?.services_title || "Practice Areas"}
          </h2>
          <p className="font-body text-[#7a7065] max-w-2xl mx-auto">
            {content?.services_subtitle || "Comprehensive legal services tailored to your needs"}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayServices.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Building;
            return (
              <div 
                key={index}
                className="index-card group cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <IconComponent className="text-[#C9A86C] mb-4 group-hover:scale-110 transition-transform" size={36} />
                <h3 className="font-heading text-lg font-semibold text-[#1a1412] mb-2">{service.title}</h3>
                <p className="font-body text-sm text-[#7a7065]">{service.description}</p>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="view-all-services-btn">
            View All Services <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden rounded-xl bg-[#1a1412]" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_jamaica-law-firm/artifacts/pdvqmtx4_image.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="relative z-10 p-10 md:p-14 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
            Ready to Discuss Your Legal Needs?
          </h2>
          <p className="font-body text-white/70 mb-8 max-w-xl mx-auto">
            Schedule a consultation with Devon Brooks and take the first step towards resolving your legal matters.
          </p>
          <Link 
            to="/book" 
            className="btn-primary inline-block"
            data-testid="cta-book-btn"
          >
            Book Your Consultation Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
